"use client";

import React, { useState } from "react";
import { cn } from "../../lib/utils";
import { CheckIcon, ChevronDown } from "lucide-react";

export function MultiSelect({ children, value = [], onValueChange }) {
  const [open, setOpen] = useState(false);

  return (
    <div className="relative">
      {React.Children.map(children, (child) => {
        if (child.type === MultiSelectTrigger) {
          return React.cloneElement(child, {
            onClick: () => setOpen(!open),
            open,
            value,
          });
        }
        if (child.type === MultiSelectContent) {
          return open
            ? React.cloneElement(child, {
              onValueChange,
              onClose: () => setOpen(false),
              selectedValues: value,
            })
            : null;
        }
        return child;
      })}
    </div>
  );
}

export function MultiSelectTrigger({ className, children, open, onClick }) {
  return (
    <button
      className={cn(
        "flex h-10 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2  ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2",
        className
      )}
      onClick={onClick}
    >
      <span className="truncate">{children}</span>
      <ChevronDown className="h-4 w-4 opacity-50" />
    </button>
  );
}

export function MultiSelectContent({
  className,
  children,
  onValueChange,
  onClose,
  selectedValues = [],
  ...props
}) {
  const toggleValue = (val) => {
    const newValues = selectedValues.includes(val)
      ? selectedValues.filter((v) => v !== val)
      : [...selectedValues, val];
    onValueChange(newValues);
  };

  return (
    <div
      className={cn(
        "absolute z-50 min-w-[8rem] rounded-md border bg-white text-popover-foreground shadow-md w-full mt-1",
        className
      )}
      {...props}
    >
      <div className="p-1 max-h-60 overflow-y-auto scrollbar-thin scrollbar-thumb-muted-foreground hover:scrollbar-thumb-ring">
        {React.Children.map(children, (child) => {
          if (child.type === MultiSelectItem) {
            return React.cloneElement(child, {
              selected: selectedValues.includes(child.props.value),
              onToggle: () => toggleValue(child.props.value),
            });
          }
          return child;
        })}
      </div>
    </div>
  );
}

export function MultiSelectItem({
  className,
  children,
  value,
  selected,
  onToggle,
  ...props
}) {
  return (
    <div
      className={cn(
        "relative flex items-center cursor-pointer select-none rounded-sm py-1.5 pl-8 pr-2  hover:bg-accent hover:text-accent-foreground",
        selected && "bg-accent text-accent-foreground",
        className
      )}
      onClick={onToggle}
      {...props}
    >
      <span
        className={cn(
          "absolute left-2 h-4 w-4 rounded-sm border border-muted-foreground flex items-center justify-center",
          selected ? "bg-primary text-primary-foreground" : ""
        )}
      >
        {selected && <CheckIcon className="h-3 w-3" />}
      </span>
      {children}
    </div>
  );
}
